// PITH back-in-stock capture. Two jobs:
//  1. Show the <back-in-stock-notify> form only while the selected variant is
//     sold out, by listening to Horizon's bubbling `variant:update` event
//     (detail.resource is the new variant JSON with `.available` and `.options`),
//     and keep the hidden `contact[tags]` per-colour tag in sync with it.
//  2. Submit the {% form 'customer' %} form over fetch() so the shopper stays on
//     the product page and sees an inline confirmation instead of being
//     redirected to /contact. Falls back to a native submit if the request
//     fails or Shopify serves a bot challenge.
// See snippets/back-in-stock.liquid.
if (!customElements.get('back-in-stock-notify')) {
  const slug = (value) =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  class BackInStockNotify extends HTMLElement {
    connectedCallback() {
      this.onVariantUpdate = this.onVariantUpdate.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      document.addEventListener('variant:update', this.onVariantUpdate);

      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:update', this.onVariantUpdate);
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    onVariantUpdate(event) {
      const variant = event.detail && event.detail.resource;
      const soldOut = variant == null || variant.available === false;
      this.hidden = !soldOut;

      if (!variant) return;

      const tagsField = this.querySelector('[data-bis-tags]');
      if (!tagsField) return;

      let tags = tagsField.dataset.bisBase; // "restock-waitlist,restock-<handle>"
      const colourIndex = parseInt(tagsField.dataset.bisColourIndex, 10);
      const handle = tagsField.dataset.bisHandle;

      if (
        Number.isInteger(colourIndex) &&
        colourIndex >= 0 &&
        Array.isArray(variant.options) &&
        variant.options[colourIndex]
      ) {
        const colour = slug(variant.options[colourIndex]);
        if (colour) tags += `,restock-${handle}-${colour}`;
      }

      tagsField.value = tags;
    }

    async onSubmit(event) {
      const form = this.form;
      if (!form) return;

      if (!form.checkValidity()) return; // let the browser show its own hints

      event.preventDefault();

      const button = form.querySelector('.ds-bis__submit');
      if (button) button.disabled = true;

      let challenged = false;
      try {
        const res = await fetch('/contact', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'text/html' },
        });
        const text = await res.text();
        challenged =
          /\/challenge(\?|$|\/)/.test(res.url) ||
          /needs to be verified|hcaptcha|cf-challenge|challenge-platform/i.test(text);
      } catch (e) {
        // Network failure — fall back to a normal submit.
        if (button) button.disabled = false;
        form.submit();
        return;
      }

      if (challenged) {
        // Can't render an interactive challenge inside fetch — hand off to the
        // browser, which will show it and complete the real submission.
        if (button) button.disabled = false;
        form.submit();
        return;
      }

      // Any non-challenge response means Shopify accepted it. The customer form
      // succeeds for new signups and already-subscribed emails alike; the email
      // was already validated client-side, so treat this as done.
      this.showSuccess();
    }

    showSuccess() {
      const prompt = this.querySelector('.ds-bis__prompt');
      const success = this.querySelector('[data-bis-success]');
      if (prompt) prompt.hidden = true;
      if (success) {
        success.hidden = false;
        success.focus();
      }
    }
  }

  customElements.define('back-in-stock-notify', BackInStockNotify);
}
